import moment from 'moment';
import { Skeleton, Tabs } from '@mantine/core';

import PageLayout from 'PageLayout';
import { EButton, EDateInput, EFormSection, EMultiSelect, EPageTitle } from 'libs/ui';

import useAutoTicketing from './Hooks/useAutoTicketing';

export default function AutoTicketing() {

    const {
        supplierList,
        airlinesList,
        getAirlinesBySuppliers,

        activeTab,
        setActiveTab,
        domesticRule,
        internationalRule,
        getCurrentRule,
        handleUpdateRule,
        saveAutoTicketingRule
    } = useAutoTicketing();

    const currentRule = getCurrentRule();

    const renderForm = (flightType: "DOMESTIC" | "INTERNATIONAL") => {
        const rule = flightType === "DOMESTIC" ? domesticRule : internationalRule;

        return (
            <div className="mt-6">
                <EFormSection
                    fullWidth
                    title={`${flightType === "DOMESTIC" ? "Domestic" : "International"} Rule Parameters`}
                    subtitle={
                        <p className="flex gap-2 items-center">
                            This rule enables auto ticketing for {flightType === "DOMESTIC" ? "domestic" : "international (any country except India)"} flights for the selected suppliers and airlines based on the start and end date (on booking date).
                        </p>
                    }
                >
                    {
                        rule.isLoading
                            ?
                            <div className="grid grid-cols-3 gap-3">
                                <Skeleton h={50} radius="md" />
                                <Skeleton h={50} radius="md" />
                                <div></div>
                                <Skeleton h={50} radius="md" />
                                <Skeleton h={50} radius="md" />
                                <div></div>
                            </div>
                            :
                            <>
                                <div className="grid grid-cols-3 gap-6">
                                    <EDateInput
                                        label="Start Date"
                                        placeholder="Start Date"
                                        minDate={new Date()}
                                        required={rule.rules.startDate === "YES"}
                                        error={rule.errors.startDate}
                                        value={rule.details.startDate ? new Date(rule.details.startDate) : undefined}
                                        onChange={(value) => {
                                            handleUpdateRule("startDate", value ? moment(value).format("YYYY-MM-DD") : null);
                                        }}
                                    />
                                    <EDateInput
                                        size="md"
                                        disabled={!rule.details.startDate}
                                        minDate={rule.details.startDate ? new Date(rule.details.startDate) : undefined}
                                        label="End Date"
                                        placeholder="End Date"
                                        required={rule.rules.endDate === "YES"}
                                        error={rule.errors.endDate}
                                        value={rule.details.endDate ? new Date(rule.details.endDate) : undefined}
                                        onChange={(value) => {
                                            handleUpdateRule("endDate", value ? moment(value).format("YYYY-MM-DD") : null);
                                        }}
                                    />
                                    <div></div>
                                </div>
                                <div className="grid grid-cols-3 gap-6">
                                    <EMultiSelect
                                        label="Suppliers"
                                        placeholder="Select"
                                        data={supplierList}
                                        required={rule.rules.supplierIds === "YES"}
                                        error={rule.errors.supplierIds}
                                        value={rule.details.supplierIds?.map((item) => String(item))}
                                        onChange={(value) => {
                                            getAirlinesBySuppliers(value);
                                            handleUpdateRule("supplierIds", value?.map(each => +each));
                                        }}
                                    />
                                    <EMultiSelect
                                        label="Airlines"
                                        placeholder="Select"
                                        data={airlinesList}
                                        required={rule.rules.airlineIds === "YES"}
                                        error={rule.errors.airlineIds}
                                        value={rule.details.airlineIds?.map((item) => String(item))}
                                        onChange={(value) => {
                                            handleUpdateRule("airlineIds", value?.map(each => +each));
                                        }}
                                    />
                                    <div></div>
                                </div>
                            </>
                    }
                </EFormSection>
            </div>
        );
    };

    return (
        <PageLayout>
            <EPageTitle
                title="Auto Ticketing"
                subTitle="Set the rules for auto ticketing"
                end={
                    <div className="flex gap-2">
                        <EButton
                            loading={currentRule.isLoading}
                            onClick={() => saveAutoTicketingRule(activeTab)}
                        >Save {activeTab === "DOMESTIC" ? "Domestic" : "International"} Rule</EButton>
                    </div>
                }
            />

            <Tabs
                value={activeTab}
                onChange={(value: string | null) => setActiveTab(value as "DOMESTIC" | "INTERNATIONAL")}
            >
                <Tabs.List>
                    <Tabs.Tab value="DOMESTIC">Domestic</Tabs.Tab>
                    <Tabs.Tab value="INTERNATIONAL">International</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="DOMESTIC">
                    {renderForm("DOMESTIC")}
                </Tabs.Panel>
                <Tabs.Panel value="INTERNATIONAL">
                    {renderForm("INTERNATIONAL")}
                </Tabs.Panel>
            </Tabs>
        </PageLayout>
    );
}